import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { fetchFeatures } from "../helpers/apiHelper";
import { useAuth } from "./AuthContext";

export const DEFAULT_FEATURE_FLAGS = Object.freeze({
  contactQuality: false,
  masterScheduleFilters: false,
  clientSearch: false,
  terminatedTab: false,
  businessClients: false,
  businessTab: false,
  coordinatorGlobalSearch: false,
  advancedClientSearch: false,
  addressEdit: false,
  addressUnmask: false,
  taxIdChange: false,
  signInModes: false,
  helpSupport: false,
});

export const normalizeFeatureFlags = (value) => {
  const features = value && typeof value === "object" ? value : {};
  return Object.keys(DEFAULT_FEATURE_FLAGS).reduce(
    (normalized, key) => ({
      ...normalized,
      [key]: features[key] === true,
    }),
    {},
  );
};

export const EMPTY_PROFILE = Object.freeze({ displayName: null, firstName: null });

// GET /features also carries the logged-in user's name (display-only).
export const normalizeProfile = (value) => {
  const source = value && typeof value === "object" ? value : {};
  const text = (field) => (
    typeof source[field] === "string" && source[field].trim() ? source[field].trim() : null
  );
  return { displayName: text("displayName"), firstName: text("firstName") };
};

const FeatureFlagsContext = createContext({
  features: DEFAULT_FEATURE_FLAGS,
  profile: EMPTY_PROFILE,
  loading: false,
  ready: true,
});

export const FeatureFlagsProvider = ({ children }) => {
  const { tokens, user, loading: authLoading } = useAuth();
  const [features, setFeatures] = useState(DEFAULT_FEATURE_FLAGS);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [loading, setLoading] = useState(false);
  const [resolvedSessionKey, setResolvedSessionKey] = useState(null);
  const requestRef = useRef(null);
  const unmountAbortTimerRef = useRef(null);
  const sessionKey =
    user?.portalUserId || user?.userId || user?.cognitoUserId || user?.email;
  const isAuthenticated = Boolean(tokens?.idToken && sessionKey);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      requestRef.current?.controller.abort();
      requestRef.current = null;
      setFeatures(DEFAULT_FEATURE_FLAGS);
      setProfile(EMPTY_PROFILE);
      setLoading(false);
      return;
    }

    if (requestRef.current?.sessionKey === sessionKey) return;

    requestRef.current?.controller.abort();
    const controller = new AbortController();
    requestRef.current = { controller, sessionKey };
    setFeatures(DEFAULT_FEATURE_FLAGS);
    setProfile(EMPTY_PROFILE);
    setLoading(true);

    fetchFeatures(tokens.idToken, controller.signal)
      .then((response) => {
        if (requestRef.current?.controller !== controller) return;
        setFeatures(normalizeFeatureFlags(response));
        setProfile(normalizeProfile(response));
      })
      .catch(() => {
        if (requestRef.current?.controller !== controller) return;
        setFeatures(DEFAULT_FEATURE_FLAGS);
        setProfile(EMPTY_PROFILE);
      })
      .finally(() => {
        if (requestRef.current?.controller === controller) {
          setLoading(false);
          setResolvedSessionKey(sessionKey);
        }
      });
  }, [authLoading, isAuthenticated, sessionKey, tokens?.idToken]);

  useEffect(
    () => {
      if (unmountAbortTimerRef.current) {
        clearTimeout(unmountAbortTimerRef.current);
        unmountAbortTimerRef.current = null;
      }

      return () => {
        // Strict Mode immediately remounts effects in development. Deferring
        // cleanup preserves the one in-flight request during that cycle.
        unmountAbortTimerRef.current = setTimeout(() => {
          requestRef.current?.controller.abort();
        }, 0);
      };
    },
    [],
  );

  // `loading` stays false between auth settling and the fetch effect starting;
  // `ready` is only true once the flags for the current session are known, so
  // route guards can wait for it instead of redirecting on the defaults.
  const ready = !authLoading && !loading && (!isAuthenticated || resolvedSessionKey === sessionKey);
  const value = useMemo(
    () => ({ features, profile, loading, ready }),
    [features, profile, loading, ready],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagsContext);
